import React, { useEffect, useState, useContext } from 'react';
import { useHistory } from "react-router-dom";
import toastError from "../../errors/toastError";
import api from "../../services/api";

import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";

import { AuthContext } from "../../context/Auth/AuthContext";

import { Button, Divider } from "@material-ui/core";
import { isNil } from 'lodash';
import ShowTicketOpen from '../ShowTicketOpenModal';
import { phoneVariations } from '../../utils/phoneVariations';

const VcardPreview = ({ contact, numbers, queueId, whatsappId, channel }) => {
    const history = useHistory();
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;

    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");

    // Inicializa com o que o parser já extraiu do vCard (nome/número). Assim o
    // card mostra o nome na hora e o botão "Conversar" só fica desabilitado se
    // realmente não houver número — sem depender do useEffect ter concluído.
    const [selectedContact, setContact] = useState({
        id: 0,
        name: contact || "",
        number: numbers || "",
        profilePicUrl: ""
    });

    // Ao montar, apenas BUSCA o contato (read-only) pra enriquecer o card com a
    // foto/id se já existir. NÃO cria nada — criar um contato só porque o vCard
    // rolou na tela gerava contatos fantasmas (POST /contacts no mount). A
    // criação acontece no clique de "Conversar" (handleNewChat).
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            const fetchContacts = async () => {
                try {
                    if (isNil(numbers) || !numbers) {
                        return
                    }
                    const number = String(numbers).replace(/\D/g, "");
                    if (!number) return;

                    // Tenta achar o contato em TODAS as variações do número (com/sem
                    // nono dígito) — o vCard manda o waid sem o 9, mas o contato pode
                    // estar salvo com o 9. Sem isso, achava que não existia e oferecia
                    // "Adicionar" (duplicando).
                    let found = null;
                    for (const variant of phoneVariations(number)) {
                        try {
                            const { data } = await api.get(`/contacts/profile/${variant}`, { params: { channel } });
                            if (data.contactId && data.contactId !== 0) {
                                found = { ...data, matchedNumber: variant };
                                break;
                            }
                        } catch { /* tenta a próxima variação */ }
                    }

                    if (found) {
                        setContact({
                            id: found.contactId,
                            name: contact || found.name || found.matchedNumber,
                            number: found.matchedNumber,
                            urlPicture: found.urlPicture
                        });
                    }
                    // Não achou em nenhuma variação → não cria; o card mostra os dados
                    // do parser e o contato é criado no clique de "Adicionar".
                } catch (err) {
                    console.log(err)
                    // Falha na BUSCA não é crítica — o card segue com os dados do parser.
                }
            };
            fetchContacts();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [companyId, contact, numbers, channel]);

    const handleCloseAlert = () => {
        setOpenAlert(false);
        setOpenAlert(false);
        setUserTicketOpen("");
        setQueueTicketOpen("");
    };

    const [saving, setSaving] = useState(false);

    // Busca o contato existente tentando as variações do número (com/sem nono
    // dígito). Retorna o objeto do contato (com id) ou null.
    const findExistingContact = async (rawNumber) => {
        for (const variant of phoneVariations(rawNumber)) {
            try {
                const { data } = await api.get(`/contacts/profile/${variant}`, { params: { channel } });
                if (data.contactId && data.contactId !== 0) {
                    return { id: data.contactId, name: data.name || selectedContact.name || variant, number: variant, urlPicture: data.urlPicture };
                }
            } catch { /* tenta a próxima */ }
        }
        return null;
    };

    // Adiciona o contato (no clique). Não cria nada no mount — evita contatos
    // fantasmas a cada vCard que rola na tela. Se o backend disser que já existe
    // (ERR_DUPLICATED_CONTACT), busca o existente e usa ele (vira "Conversar").
    const handleAddContact = async () => {
        const number = String(selectedContact.number || numbers || "").replace(/\D/g, "");
        if (!number || saving) return;
        setSaving(true);
        try {
            const { data } = await api.post("/contacts", {
                name: selectedContact.name || number,
                number,
                email: "",
                companyId,
            });
            setContact(data); // agora tem id → o botão vira "Conversar"
        } catch (err) {
            // Já existe → acha o existente e usa (não mostra erro ao atendente).
            const existing = await findExistingContact(number);
            if (existing) {
                setContact(existing);
            } else {
                toastError(err);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleNewChat = async () => {
        try {
            // Se ainda não existe contato, adiciona antes de abrir o ticket.
            let contactId = selectedContact.id;
            if (!contactId) {
                const number = String(selectedContact.number || numbers || "").replace(/\D/g, "");
                if (!number) return;
                const { data } = await api.post("/contacts", {
                    name: selectedContact.name || number,
                    number,
                    email: "",
                    companyId,
                });
                contactId = data.id;
                setContact(data);
            }

            const { data: ticket } = await api.post("/tickets", {
                contactId,
                userId: user.id,
                status: "open",
                queueId,
                companyId: companyId,
                whatsappId
            });

            history.push(`/tickets/${ticket.uuid}`);
        } catch (err) {
            const ticket = JSON.parse(err.response.data.error);

            if (ticket.userId !== user?.id) {
                setOpenAlert(true);
                setUserTicketOpen(ticket?.user?.name);
                setQueueTicketOpen(ticket?.queue?.name);
            } else {
                setOpenAlert(false);
                setUserTicketOpen("");
                setQueueTicketOpen("");

                history.push(`/tickets/${ticket.uuid}`);
            }
        }
    }

    return (
        <>
            <div style={{
                minWidth: "250px",
            }}>
                <ShowTicketOpen
                    isOpen={openAlert}
                    handleClose={handleCloseAlert}
                    user={userTicketOpen}
                    queue={queueTicketOpen}
                />
                <Grid container spacing={1}>
                    <Grid item xs={2}>
                        <Avatar src={`${selectedContact?.urlPicture}`} />
                    </Grid>
                    <Grid item xs={9}>
                        <Typography
                            style={{ marginTop: "12px", marginLeft: "10px" }}
                            color="primary"
                            variant="subtitle1"
                            gutterBottom
                        >
                            {selectedContact.name || selectedContact.number}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider />
                        {selectedContact.id ? (
                            <Button
                                fullWidth
                                color="primary"
                                onClick={handleNewChat}
                                disabled={!selectedContact.number}
                            >Conversar</Button>
                        ) : (
                            <Button
                                fullWidth
                                color="primary"
                                onClick={handleAddContact}
                                disabled={!selectedContact.number || saving}
                            >{saving ? "Adicionando…" : "Adicionar"}</Button>
                        )}
                    </Grid>
                </Grid>
            </div>
        </>
    );

};

export default VcardPreview;